import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { genericGet } from "../../src/services/apiServices";
import SubscriptionPlans from "./Subscription";
import { ENQUIRY_SUBSCRIPTION_PLAN_ACTIVE, SUBSCRIPTION_HISTORY } from "../services/apiRoutes";
import { CREATE_ORDER, SAVE_ADDON_PAYMENT_INFO } from "../services/apiRoutes";
import { genericPost } from "../services/authServices";
import DataTable from "../components/common/DataTable"; // Import your DataTable component
import CustomMaster from "../components/common/CustomMaster";
import CustomButton from "../components/common/CustomButton";

const SubscriptionHistory = () => {
  const [subscriptionHistoryList, setSubscriptionHistoryList] = useState([]);
  const [value, setValue] = useState(0);
  const [latestSubscription, setLatestSubscription] = useState(null);
  const [additionalUsers, setAdditionalUsers] = useState(0);
  const [totalPerUserPrice, setTotalPerUserPrice] = useState(0);
  const [subscriptionType, setSubscriptionType] = useState("yearly");
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [additionalCompany, setAdditionalCompany] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [id, setId] = useState()

  const handleUserChange = (event) => {
    const users = parseInt(event.target.value, 10) || 1;
    setAdditionalUsers(users);
    updateTotalPrice(subscriptionType, users);
  };
  const handleCompanyChange = (event) => {
    const companies = parseInt(event.target.value, 10) || 0;
    setAdditionalCompany(companies);
    updateTotalPrice(subscriptionType, additionalUsers, companies);
  };
  const updateTotalPrice = (type, users = 0, companies = 0) => {
    let userAmount = type === "yearly"
      ? (users * yearlyPerUserPrice)
      : (users * monthlyPerUserPrice);
    let companyAmount = type === "yearly"
      ? (companies * companyPerYear)
      : (companies * companyPerMonth);
    let total = userAmount + companyAmount;
    setTotalPerUserPrice(total);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    const isRazorpayLoaded = await loadRazorpayScript();
    if (!isRazorpayLoaded) {
      alert(
        "Failed to load Razorpay SDK. Please check your internet connection."
      );
      return;
    }

    try {
      const response = await genericPost(CREATE_ORDER, { amount: totalPerUserPrice });
      if (response.error) {
        throw new Error(response.error || "Error creating order.");
      }

      const orderData = response.data;
      const options = {
        key: "rzp_test_dD0jhbnTbrfcD3", // Replace with your Razorpay Key ID
        amount: totalPerUserPrice * 100,
        currency: "USD",
        name: "Exim CRM",
        description: `${subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1)
          } Subscription Payment`,
        order_id: orderData.id,
        handler: async (paymentResponse) => {
          alert(
            `Payment successful! Payment ID: ${paymentResponse.razorpay_payment_id}`
          );

          const paymentInfo = {
            orderId: orderData.id,
            razorpayPaymentId: paymentResponse.razorpay_payment_id,
            razorpayOrderId: paymentResponse.razorpay_order_id,
            razorpaySignature: paymentResponse.razorpay_signature,
            subscriptionId: subscriptionType === "yearly" ? "1" : "2",
            totalUsers: additionalUsers,
            totalPrice: totalPerUserPrice,
            subscriptionPkId: latestSubscription?.id || '',
            addon: 1
          };

          const saveResponse = await genericPost(
            SAVE_ADDON_PAYMENT_INFO,
            paymentInfo
          );
          if (saveResponse.status === 201) {
            alert("Payment and subscription information saved successfully!");
          } else {
            alert("Failed to save payment information.");
          }
        },
        prefill: {
          name: "Test User",
          email: "user@example.com",
        },
        theme: {
          color: "#3399cc",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to create order. Please try again later.");
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getSubscriptionHistoryList = async () => {
    try {
      const response = await genericGet({ url: SUBSCRIPTION_HISTORY });
      let subscriptionHistoryList = response.data.list;

      if (subscriptionHistoryList && subscriptionHistoryList.length > 0) {
        const filteredList = subscriptionHistoryList.filter(
          (item) => item.addonValue != 1
        );

        if (filteredList.length > 0) {
          const mappedList = filteredList.map((item) => ({
            ...item,
            planName: item?.PlanType?.planName || "",
            currencySymbol: item?.currencyList?.currency_symbol || "",
            currencyName: item?.currencyList?.currency_name || "",
            createdAtFormatted: new Date(item.createdAt).toLocaleString(),
            totalPrice: `${item?.currencyList?.currency_symbol || ""}${parseFloat(item?.totalPrice || 0).toFixed(2)}`,
            planDuration: item.subscriptionId === 2 ? 'monthly' : item.subscriptionId === 1 ? 'yearly' : '',
            planDurationTime: item.subscriptionId === 2 ? '1 Monthly' : item.subscriptionId === 1 ? '12 Monthly' : '',
          }));
          setSubscriptionHistoryList(mappedList);

          const sortedList = [...mappedList].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          setLatestSubscription(sortedList[0]);
          setSubscriptionType(sortedList[0].planDuration);
        } else {
          setSubscriptionHistoryList([]);
          setLatestSubscription(null);
        }
      } else {
        setSubscriptionHistoryList([]);
        setLatestSubscription(null);
      }
    } catch (error) {
      console.log("Error fetching subscription history:", error);
    }
  };

  useEffect(() => {
    getSubscriptionHistoryList();
  }, [id]);

  const columns = useMemo(
    () => [

      {
        accessorKey: "index",
        header: "SN.",
        size: 50,
        tableHeader: { alignItems: "center" },
        tableStyle: { textAlign: "center" },
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ row }) => String(row.index + 1).padStart(2, "0"),
      },
      {
        accessorKey: "planName",
        header: "plan Name", tableStyle: { textAlign: "center" },
      },
      {
        accessorKey: "planDuration",
        header: "plan Duration", tableStyle: { textAlign: "center" },
      },
      {
        accessorKey: "totalPrice",
        header: "Total Price", tableStyle: { textAlign: "center" },
      },
      {
        accessorKey: "endDate",
        header: "Expire Date", tableStyle: { textAlign: "center" },
      },
      {
        accessorKey: "totalUsers",
        header: "Total Users", tableStyle: { textAlign: "center" },
      },
      {
        accessorKey: "totalCompany",
        header: "Total Company", tableStyle: { textAlign: "center" },
      },

      {
        accessorKey: "razorpayPaymentId",
        header: "Razorpay Payment ID", tableStyle: { textAlign: "center" },
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
      },
    ],
    []
  );

  const calculateRemainingDay = (endDate) => {
    const today = new Date();
    const expiryDate = new Date(endDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  useEffect(() => {
    getSubscriptionHistoryList();
  }, [id]);

  useEffect(() => {
    if (latestSubscription?.plan) {
      fetchSubscriptionPlans(latestSubscription.plan);
    }
  }, [latestSubscription]);

  useEffect(() => {
    if (latestSubscription) {
      updateTotalPrice(subscriptionType, additionalUsers, additionalCompany);
    }
  }, [additionalUsers, additionalCompany, subscriptionType, latestSubscription]);


  const selectedPlan = subscriptionPlans.length > 0 ? subscriptionPlans[0] : null;
  const monthlyPerUser = selectedPlan ? parseFloat(selectedPlan.perMonthAmount) : 0;
  const companyPerYear = selectedPlan ? parseFloat(12 * (selectedPlan.companyPerMonth)) : 0;
  const companyPerMonth = selectedPlan ? parseFloat(selectedPlan.companyPerMonth) : 0;
  const currencySymbol = selectedPlan ? selectedPlan.currencySymbol : '';
  const planName = selectedPlan ? selectedPlan.planName : '';
  const yearlyPerUserPrice = selectedPlan ? parseFloat(12 * (selectedPlan.perMonthAmount)) : 0;
  const monthlyPerUserPrice = selectedPlan ? parseFloat(selectedPlan.perMonthAmount) : 0;

  const fetchSubscriptionPlans = async (planId) => {
    setLoading(true);
    try {
      const payload = {
        plan: planId,
      };

      const response = await genericPost(ENQUIRY_SUBSCRIPTION_PLAN_ACTIVE, payload);

      if (response.data.success && Array.isArray(response.data.SubscriptionPlan)) {
        const planData = response.data.SubscriptionPlan.map((plan, index) => ({
          id: plan.subscriptionPlanId,
          index: (index + 1).toString().padStart(2, '0'),
          planName: plan.PlanType?.planName || "",
          baseAmount: plan.baseAmount,
          currencySymbol: plan.currencyList?.currency_symbol || "",
          perMonthAmount: plan.perMonthAmount,
          companyPerMonth: plan.companyPerMonth,
          planActiveDate: plan.planActiveDate,
          planExpiredDate: plan.planExpiredDate || '',
          subscriptionPlanId: plan.subscriptionPlanId || "",
          planActiveStatus: plan.planActiveStatus || "",

        }));
        setSubscriptionPlans(planData);
      } else {
        setError('No subscription plans found');
      }
    } catch (error) {
      setError('Error fetching subscription plans');
      console.error('Error fetching subscription plans:', error);
    } finally {
      setLoading(false);
    }
  };
  const totalUserAmount = subscriptionType === "yearly"
    ? (parseInt(additionalUsers || 0) * yearlyPerUserPrice)
    : (parseInt(additionalUsers || 0) * monthlyPerUserPrice);

  const totalCompanyAmount = subscriptionType === "yearly"
    ? (parseInt(additionalCompany || 0) * companyPerYear)
    : (parseInt(additionalCompany || 0) * companyPerMonth);



  const defaultView = 'subscriptionHistory';
  const mainContent = (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab
              label="Active Subscription"
              {...a11yProps(0)}
              sx={{
                fontWeight: "bold",
                textTransform: "capitalize",
                color: value === 0 ? "#007BFF" : "inherit",
              }}
            />
            <Tab
              label="Buy Subscription"
              {...a11yProps(1)}
              sx={{
                fontWeight: "bold",
                textTransform: "capitalize",
                color: value === 1 ? "#007BFF" : "inherit",
              }}
            />
            <Tab
              label="Subscription Purchase History"
              {...a11yProps(2)}
              sx={{
                fontWeight: "bold",
                textTransform: "capitalize",
                color: value === 2 ? "#007BFF" : "inherit",
              }}
            />
            <Tab
              label="Buy Users and Company"
              {...a11yProps(3)}
              sx={{
                fontWeight: "bold",
                textTransform: "capitalize",
                color: value === 3 ? "#007BFF" : "inherit",
              }}
            />
          </Tabs>
        </Box>

        <CustomTabPanel value={value} index={0}>
          {latestSubscription ? (
            <>
              <Grid
                container
                spacing={4}
                justifyContent="center"
                sx={{ marginTop: 2 }}
              >
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      boxShadow: 3,
                      transition: "0.3s",
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h2"
                        color="primary"
                        sx={{ fontWeight: "bold", marginBottom: 2 }}
                      >
                        Active Subscription
                      </Typography>

                      <Divider sx={{ marginBottom: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body1">
                            <strong>Subscription Type:</strong>{" "}
                            {latestSubscription.subscriptionId == 1
                              ? "Yearly"
                              : "Monthly"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1">
                            <strong>Total Price: </strong>
                            {latestSubscription.totalPrice}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1">
                            <strong>Total Users:</strong>{" "}
                            {latestSubscription.totalUsers}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1">
                            <strong>Total Company:</strong>{" "}
                            {latestSubscription.totalCompany}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1">
                            <strong>Purchased On:</strong>{" "}
                            {latestSubscription.startDate}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1">
                            <strong>Expired On:</strong>{" "}
                            {latestSubscription.endDate}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body1">
                            <strong>Razorpay Payment ID:</strong>{" "}
                            {latestSubscription.razorpayPaymentId || "N/A"}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ marginBottom: 2, marginTop: 2 }} />

                      <Button variant="contained" color="primary" fullWidth>
                        Subsription Will Expired in{" "}
                        {latestSubscription.endDate && (
                          <>
                            {calculateRemainingDay(latestSubscription.endDate) > 0
                              ? calculateRemainingDay(latestSubscription.endDate)
                              : 'Expired'}
                          </>
                        )}
                        &nbsp;  days
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ padding: "20px", textAlign: "center" }}
            >
              No active subscription found.
            </Typography>
          )}
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <Typography style={{ marginLeft: "-25%" }}>
            <SubscriptionPlans setValue={setValue} setId={setId} />
          </Typography>
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2}>
          <DataTable
            title="Subscription Purchase History"
            columns={columns}
            data={subscriptionHistoryList}
            handleOpen={() => console.log("Handle open clicked")}
          />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={3}>
          {latestSubscription && (
            <Card variant='outlined'>
              <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} md={8}>

                  <CardContent>
                    <Typography
                      variant="h4"
                      color="primary"
                      sx={{ fontWeight: "bold", marginBottom: 2 }}
                    >
                      Add Users and Company To Your Exisiting Subcription Plan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 5, flexGrow: 1 }}>
                        <Typography>Additional Users</Typography>
                        <Typography sx={{ fontWeight: 'bold' }}> {currencySymbol} {monthlyPerUser}/user</Typography>
                        <Typography>x <input
                          type="number"
                          value={additionalUsers}
                          onChange={handleUserChange}
                          style={inputStyle}
                        /> users x {latestSubscription.planDurationTime} </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {currencySymbol} {totalUserAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <br />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 5, flexGrow: 1 }}>
                        <Typography>Additional Company</Typography>
                        <Typography sx={{ fontWeight: 'bold' }}>{currencySymbol} {companyPerMonth}/Com.</Typography>
                        <Typography>x <input
                          type="number"
                          value={additionalCompany}
                          onChange={handleCompanyChange}
                          style={inputStyle}
                        /> Comp. x {latestSubscription.planDurationTime}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {currencySymbol} {totalCompanyAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <br />
                    <Divider />
                    <br />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Typography style={{ textTransform: "capitalize" }}> {latestSubscription.planDuration}  Total Subscription Amount</Typography>
                      <Typography sx={{ fontWeight: 'bold' }}>{currencySymbol} {Number(totalPerUserPrice).toFixed(2)}</Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom color="primary"
                      sx={{ fontSize: '10px', textTransform: 'capitalize' }}
                    >
                      Note: Price is applied per user and Company  with the remaining subscription days
                    </Typography>
                  </CardContent>

                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{
                    height: "100%",
                    backgroundColor: "#E7F0FF",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography sx={{ fontWeight: 'bold', color: '#007BFF', fontSize: "25px", textTransform: "capitalize" }}>{currencySymbol} {Number(totalPerUserPrice).toFixed(2)} /{latestSubscription.planDuration}  </Typography>
                      <br />
                      <CustomButton
                        onClick={handlePayment}
                        variant="contained" color="primary"
                        title="Buy Now"
                      />
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Card>
          )}
        </CustomTabPanel>

      </Box>
    </>
  );
  return (
    <>
      <CustomMaster
        defaultView={defaultView}
        mainContent={mainContent} />
    </>
  );
};

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export default SubscriptionHistory;

const inputStyle = {
  backgroundColor: "#e7effe",
  border: "1px solid #00acff",
  borderRadius: "4px",
  color: "#0f4ec2",
  fontWeight: 600,
  height: "20px",
  // margin: "20px",
  width: "70px",
};